import { Entity, mapEntity } from '@toggle/toggle';
import { useTranslation } from 'react-i18next';

import { formatPriceDisplay } from '~/components/price-display-view/helpers/price-display-helpers';
import { mapEntityToPriceEntity, useLivePrice } from '~/hooks/use-live-price';
import { globalPriceFormatter } from '~/utils/asset-price/assetPrice';
import { getPriceData } from '~/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';

import { useSnakeMeta } from '../snakes/useSnake';
import { usePreliminaryPrice } from './usePreliminaryPrice';

export interface UsePriceDisplayReturn {
  isLoading: boolean;
  isValid: boolean;
  priceData: {
    price: ReturnType<typeof formatPriceDisplay>;
    mappedEntity: ReturnType<typeof mapEntity>;
    priceFormatter: (price: number) => string;
  } | null;
  time?: string;
}

export const usePriceDisplay = (entity: Entity): UsePriceDisplayReturn => {
  const { t } = useTranslation(['analyze']);
  const mappedEntity = mapEntity(entity);

  const { data: snakeMeta, isLoading: isSnakeMetaLoading } = useSnakeMeta(
    entity?.default_snake
  );
  const { data: preliminaryPrice, isLoading: isPreliminaryPriceLoading } =
    usePreliminaryPrice(entity.subscribable_ticker ?? '');

  const priceEntities = mapEntityToPriceEntity(entity, snakeMeta);
  const livePriceData = useLivePrice(priceEntities);
  const livePriceInfo = getPriceData({
    hoverData: undefined,
    livePriceData,
    entity: mappedEntity,
    ts: [],
    priceEntity: priceEntities[0],
  });

  const isLoading = isPreliminaryPriceLoading || isSnakeMetaLoading;

  const isValid = !!(
    preliminaryPrice &&
    (livePriceInfo || snakeMeta?.last_value !== undefined) &&
    snakeMeta?.last_timestamp
  );

  if (!isValid) {
    return {
      isLoading,
      isValid: false,
      priceData: null,
    };
  }

  const priceFormatter = (price: number) =>
    globalPriceFormatter({
      entity: mappedEntity,
      price,
      priceSuffix: '',
      shouldShowCurrency: false,
    });

  const price = formatPriceDisplay({
    exchange: mappedEntity.exchange.code,
    livePriceInfo,
    preliminaryPrice: preliminaryPrice.data,
    meta: snakeMeta,
    isHovered: false,
    t,
  });

  return {
    isLoading,
    isValid: true,
    priceData: {
      price,
      mappedEntity,
      priceFormatter,
    },
    time: livePriceInfo.time,
  };
};
